import React from "react";
import { useState } from "react";
import {
  Share2,
  X,
  Copy,
  Facebook,
  Linkedin,
  Quote,
  Music4,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { blogTbcarrer } from "i18n/blog";
import { useSelector } from "react-redux";
import { fadeUp, slideLeft, slideRight } from "../animations/variants";
import ShareModal from "components/Blog/ShareModal";
import { Helmet } from "react-helmet-async";
import ScrollToTop from "Util/ScrollToTop";
import PhotoGallery from "components/Blog/PhotoGallery";
import { serviceBlog } from "i18n/serviceBlog"; // adjust path
import { AnimatePresence, motion } from "framer-motion";
import usePageSEO from "hook/UsePageSEO";
import { Link } from "react-router-dom";

const BlogDetail = () => {
  const { id } = useParams();
  const lang = useSelector((state) => state.language.lang);

  const blog = blogTbcarrer.find((item) => item.id === Number(id));
  const t = blog?.[lang];

  const shareUrl = `https://tbcareer.com/blog/${blog.id}`;

  const serviceblog = serviceBlog[lang] || serviceBlog.en;

  const seoTitle = t?.title || "TB Career Blog";

  const seoDescription =
    t?.intro?.[0] || "Career development blog by TB Career";

  const imagePath = blog.seoImage || blog.mainImage;

  const seoImage = imagePath.startsWith("http")
    ? imagePath
    : `https://tbcareer.com${imagePath}`;

  usePageSEO({
    title: seoTitle,
    description: seoDescription,
    ogTitle: seoTitle,
    ogDescription: seoDescription,
    ogImage: seoImage,
    ogUrl: shareUrl,
    ogSite_name: "TB Career",
  });
  const [openShare, setOpenShare] = useState(false);
  const [showAllPosts, setShowAllPosts] = useState(false);

  const navigate = useNavigate();

  if (!blog) return <div>Not found</div>; // ❌ EARLY RETURN AFTER HOOK

  // ✅ SEO HOOK

  const formatDate = (dateString, lang) => {
    const date = new Date(dateString);

    const day = date.getDate();

    let month;

    if (lang === "vi") {
      month = `Thg ${date.getMonth() + 1}`;
    } else {
      month = date.toLocaleString("en-US", { month: "short" });
    }

    return { day, month };
  };

  const { day, month } = formatDate(blog.date, lang);

  const reversedBlogs = [...blogTbcarrer].reverse();

  const visibleBlogs = showAllPosts ? reversedBlogs : reversedBlogs;

  const galleryImages = [
    blog.mainImage,
    ...(blog.images || []).filter((img) => img !== blog.mainImage),
  ];

  const isDesktop = window.innerWidth >= 768;

  const truncateWords = (text, maxWords = 3) => {
    const words = text.split(" ");
    return words.length > maxWords
      ? `${words.slice(0, maxWords).join(" ")}...`
      : text;
  };

  if (!blog) return <div>Not found</div>;

  return (
    <>
      <div className="bg-darkbg overflow-hidden">
        {/* HERO */}
        <div className="h-[400px] lg:h-[500px] flex flex-col justify-center items-center text-white text-center relative">
          <div
            className="absolute inset-0 bg-cover bg-center grayscale"
            style={{
              backgroundImage: "url(/images/tb1.jpg)",
            }}
          />

          <div className="absolute inset-0 bg-black/40"></div>

          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUp}
            className="relative z-10 flex flex-col items-center justify-center"
          >
            {/* TITLE */}
            <h1 className="text-3xl font-bold mb-6 tracking-widest uppercase">
              Blog Details
            </h1>

            <div
              className="
    inline-flex flex-wrap items-center
    gap-x-2 gap-y-1
    px-5 py-2.5
    rounded-full
    border border-white/50
    bg-white/10
    backdrop-blur-md
    text-sm text-white
    max-w-full
  "
            >
              <span
                onClick={() => navigate("/")}
                className="cursor-pointer hover:text-sky-300 transition"
              >
                Home
              </span>
              <span className="opacity-60">»</span>
              <span
                onClick={() => navigate("/blogs")}
                className="cursor-pointer hover:text-sky-300 transition"
              >
                Blogs
              </span>
              <span className="opacity-60">»</span>
              <span className="lg:hidden font-medium" title={t.title}>
                {truncateWords(t.title, 5)}
              </span>

              <span className="hidden lg:inline font-medium" title={t.title}>
                {t.title}
              </span>
            </div>
          </motion.div>
        </div>

        {/* CONTENT */}
        <div className="max-w-6xl mx-auto px-6 py-16 grid lg:grid-cols-3 gap-10">
          {/* LEFT */}
          <motion.div
            variants={slideLeft}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="lg:col-span-2"
          >
            {/* IMAGE */}
            <div className="relative rounded-2xl overflow-hidden">
              <PhotoGallery images={galleryImages} hideGrid>
                <motion.img
                  src={blog.mainImage}
                  alt={t.title}
                  initial={{ scale: 1.1 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 1.2 }}
                  style={{
                    objectPosition: isDesktop
                      ? blog.objectPositionDesktop || "center"
                      : blog.objectPositionMobile || "center",
                  }}
                  className={`
        w-full
        h-[280px]
        cursor-zoom-in
        transition duration-500
        hover:scale-[1.02]
        ${
          blog.full
            ? `
              object-contain
              bg-darkbg
              md:object-cover
              md:bg-transparent
            `
            : `object-cover`
        }
      `}
                />
              </PhotoGallery>
              {/* DATE */}
              <motion.div
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="absolute top-0 lg:top-3 left-3 bg-navy border-4 border-darkbg text-white px-4 py-2 rounded-xl text-center shadow-md"
              >
                <p className="text-lg font-bold">{day}</p>
                <p className="text-xs uppercase">{month}</p>
              </motion.div>
            </div>

            <motion.div
              variants={fadeUp}
              className="mt-10 flex items-center justify-between border-0 pt-0"
            >
              {/* TAGS */}
              <div className="flex gap-3 flex-wrap text-text">
                <span className="px-4 py-1 border border-navy rounded-full text-sm">
                  By Admin
                </span>
                <span>•</span>
                <span className="px-4 py-1 border border-navy rounded-full text-sm">
                  {t.section}
                </span>
              </div>

              {/* SHARE BUTTON */}
              <button
                onClick={() => setOpenShare(true)}
                className="flex items-center gap-2 text-sky-500 hover:gap-3 transition"
              >
                <Share2 size={18} />
                Share
              </button>
            </motion.div>

            {/* TITLE */}
            <motion.h2
              variants={fadeUp}
              className="text-2xl md:text-3xl font-bold mt-6 text-neonYellow"
            >
              {t.title}
            </motion.h2>

            {blog.imageTitle && (
              <motion.img
                variants={fadeUp}
                src={blog.imageTitle}
                alt="blog visual"
                className="w-full max-w-[200px] mb-4 border border-neonYellow rounded-xl mt-3 cursor-pointer hover:opacity-80 transition"
                onClick={() =>
                  window.open(
                    "https://cdanz.org.nz/PersonProfile?Action=View&PersonProfile_id=638",
                    "_blank",
                  )
                }
              />
            )}

            {/* CONTENT */}
            <motion.div
              variants={fadeUp}
              className="mt-6 space-y-6 text-gray-700 leading-relaxed"
            >
              {/* INTRO */}
              {t.intro.map((p, i) => (
                <motion.p key={i} variants={fadeUp}>
                  {p}
                </motion.p>
              ))}

              {/* HIGHLIGHT */}
              <motion.p
                variants={fadeUp}
                className="font-semibold text-lg text-text"
              >
                {t.highlight}
              </motion.p>

              <motion.div variants={fadeUp}>
                <div
                  className="
      relative overflow-hidden
      mt-10
      rounded-3xl
      border border-gray-200
      bg-gradient-to-br from-white via-gray-50 to-yellow-50
      p-8 md:p-10
      shadow-lg
    "
                >
                  {/* Decorative Quote */}
                  <div
                    className="
        absolute -top-4 -left-2
        text-neonYellow/15
      "
                  >
                    <Quote size={120} strokeWidth={1.5} />
                  </div>

                  {/* Accent Line */}
                  <div
                    className="
        absolute top-0 left-0
        h-full w-2
        bg-gradient-to-b
        from-neonYellow
        to-amber-500
      "
                  />

                  {/* Content */}
                  <div className="relative z-10">
                    <span
                      className="
          inline-flex items-center gap-2
          px-4 py-1.5
          rounded-full
          bg-neonYellow/10
          text-neonYellow
          text-xs font-semibold uppercase tracking-wider
          mb-5
        "
                    >
                      Inspirational conclusion{" "}
                    </span>

                    <div className="space-y-4">
                      {t.closing.map((item, i) => {
                        if (typeof item === "string") {
                          return (
                            <p
                              key={i}
                              className="text-gray-700 leading-relaxed text-md italic"
                            >
                              {item}
                            </p>
                          );
                        }

                        return (
                          <Link
                            key={i}
                            to={item.to}
                            className="
      inline-flex
      items-center
      mt-5
      text-xs
      uppercase
      tracking-[0.25em]
      text-gray-500
      hover:text-gray-900
      transition-colors
    "
                          >
                            {item.text}
                          </Link>
                        );
                      })}
                    </div>
                  </div>

                  {/* Bottom Signature */}
                  <div
                    className="
        mt-8 pt-6
        border-t border-gray-200
        flex items-center justify-between
      "
                  >
                    <div>
                      <p className="font-semibold text-gray-900">tbCareer</p>
                      <p className="text-sm text-gray-500">
                        Career Development • Migrant Success • Community Impact
                      </p>
                    </div>

                    <Quote className="text-neonYellow/40" size={32} />
                  </div>
                </div>
              </motion.div>
              {/* VIBE */}
              <motion.div variants={fadeUp}>
                <h3 className="font-semibold text-lg mb-2">{t.vibeTitle}</h3>
                <ul className="list-disc pl-5 space-y-1">
                  {t.vibe.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </motion.div>

              {/* TIPS */}
              <motion.div variants={fadeUp}>
                <h3 className="font-semibold text-lg mb-4">{t.tipsTitle}</h3>

                <div className="space-y-6">
                  {t.tips.map((tip, i) => (
                    <div key={i}>
                      <h4 className="font-semibold text-text mb-2">
                        {i + 1}. {tip.title}
                      </h4>

                      <ul className="list-disc pl-5 space-y-1">
                        {tip.items.map((item, j) => (
                          <li key={j}>{item}</li>
                        ))}
                      </ul>

                      {/* LINK (only if exist) */}
                      {tip.link && (
                        <a
                          href={tip.link}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sky-500 text-sm mt-2 inline-block hover:underline"
                        >
                          {tip.link}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* REFLECTION */}

              <motion.div
                variants={fadeUp}
                className="mt-12 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-orange-50 p-8 shadow-lg"
              >
                <div className="mb-6">
                  <h4 className="font-semibold text-text mb-2">
                    {t.reflectionTitle}
                  </h4>
                </div>

                <div className="space-y-5">
                  {t.reflection.map((item, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.08 }}
                      className="flex gap-4"
                    >
                      <div className="mt-2 w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" />

                      <p className="leading-8 text-gray-700">{item}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* CLOSING */}
            </motion.div>
            {blog.link && (
              <div className="relative bg-gray-100 p-6 rounded-2xl mt-6 border-l-4 border-neonYellow">
                <p className="text-sm text-gray-600 mb-2">
                  {lang === "vi"
                    ? "Xem hồ sơ chính thức của mình tại CDANZ:"
                    : "View my official CDANZ profile:"}
                </p>

                <a
                  href={blog.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-lg font-semibold text-navy hover:underline"
                >
                  {lang === "vi"
                    ? "👉 Xem hồ sơ tại đây"
                    : "👉 View profile here"}
                </a>
              </div>
            )}
            {/* GALLERY */}

            <PhotoGallery images={blog.images || []} />
          </motion.div>

          {/* RIGHT SIDEBAR */}
          <motion.div
            variants={slideRight}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="space-y-6"
          >
            {/* SEARCH */}

            {/* SERVICES */}
            <motion.div
              variants={fadeUp}
              className="bg-white p-6 rounded-xl shadow"
            >
              <h3 className="font-semibold mb-3 text-neonYellow">
                {serviceblog.services}
              </h3>

              <ul className="space-y-2 text-gray-600">
                {[
                  { key: "coaching", link: "https://www.tbcareer.com" },
                  { key: "webDesign", link: "https://www.miraelite.com" },
                  {
                    key: "aiServices",
                    link: "https://www.miraelite.com/ai-service",
                  },
                  { key: "booking", link: "https://www.caljn.com" },
                  { key: "orderOnline", link: "https://www.odijn.com" },
                ].map((item, i) => (
                  <li key={i}>
                    <a
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-sky-500 transition"
                    >
                      {serviceblog[item.key]}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>
            {/* RECENT */}
            <motion.div
              variants={fadeUp}
              className="bg-white p-6 rounded-xl shadow"
            >
              <h3 className="font-semibold mb-3 text-neonYellow">
                Recent Post
              </h3>

              <div
                className="
    space-y-4
    max-h-[350px]
    overflow-y-auto
    pr-2
    scrollbar-thin
    scrollbar-thumb-neutral-300
    scrollbar-track-transparent
    scroll-smooth
  "
              >
                {" "}
                <AnimatePresence initial={false}>
                  {visibleBlogs.map((b, index) => (
                    <motion.div
                      key={b.id}
                      layout
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{
                        duration: 0.35,
                        delay: index * 0.04,
                      }}
                      onClick={() => navigate(`/blog/${b.id}`)}
                      className="
          flex gap-3
          cursor-pointer
          group text-text
        "
                      whileHover={{ x: 5 }}
                    >
                      <div className="w-16 h-16 rounded overflow-hidden flex-shrink-0">
                        <img
                          src={b.mainImage}
                          alt={b[lang].title}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      <p
                        className="
            text-sm font-medium
            group-hover:text-sky-500
            transition
            line-clamp-2
          "
                      >
                        {b[lang].title}
                      </p>
                    </motion.div>
                  ))}
                </AnimatePresence>
                {/* Toggle Button */}
                {reversedBlogs.length > 5 && (
                  <motion.button
                    whileTap={{ scale: 0.96 }}
                    whileHover={{ y: -1 }}
                    onClick={() => setShowAllPosts(!showAllPosts)}
                    className="
        text-sm font-medium
        text-reddark hover:text-red-500
        transition-all duration-300
      "
                  >
                    {showAllPosts
                      ? lang === "vi"
                        ? "Thu gọn ↑"
                        : "Show Less ↑"
                      : lang === "vi"
                        ? "Xem thêm ↓"
                        : "Show More ↓"}
                  </motion.button>
                )}
              </div>
            </motion.div>
          </motion.div>
        </div>
        <ShareModal
          open={openShare}
          onClose={() => setOpenShare(false)}
          url={shareUrl}
        />
      </div>
    </>
  );
};

export default BlogDetail;
